import React, { useEffect, useState } from 'react';
import BarChart from './BarChart';
import { fetchJson } from './adminApi';
import { duration, fmt } from './adminFormat';

// Product analytics from the site's own event tracking (src/analytics, backend analytics.py):
// who comes, what they use, what they search and filter for, which listings they open and click through.
// Split into sub-views (one topic each) rather than one long page; the period selector is shared by all.

const PERIODS = [7, 30, 90];

// The sub-views (a secondary tab bar). Each shows one topic.
const VIEWS = [
    { id: 'overview', label: 'Overview' },
    { id: 'search', label: 'Search' },
    { id: 'filters', label: 'Filters & map' },
    { id: 'areacards', label: 'Area cards' },
    { id: 'listings', label: 'Listings' },
    { id: 'audience', label: 'Audience' },
    { id: 'health', label: 'Health' },
];

// What each tracked event means, in plain words (the feature-adoption table).
const EVENT_LABELS = {
    'search.select': 'Searched and picked an area',
    'search.no_match': 'Search with no match',
    'search.remove': 'Removed a search',
    'search.merged': 'Bigger search replaced smaller ones',
    'map.area_toggle': 'Selected / deselected an area on the map',
    'map.area_popup': 'Opened an area popup (double click)',
    'map.area_compare': 'Pinned area cards (double click)',
    'map.metric': 'Changed map metric',
    'map.listings_toggle': 'Hid / showed listings on the map',
    'map.recenter': 'Re-centred the map',
    'map.close_popups': 'Closed all popups',
    'filter.apply': 'Applied listing filters',
    'filter.reset': 'Reset listing filters',
    'filter.drawer_open': 'Opened the filter drawer (kartvy)',
    'listing.open': 'Opened a listing',
    'listing.photos': 'Browsed listing photos',
    'listing.close': 'Closed a listing',
    'listing.realtor_click': 'Clicked through to the realtor',
    'feed.page': 'Paged the listing feed',
    'feed.tab': 'Switched for-sale / coming tab',
    'calc.change': 'Used the housing-cost calculator',
    'info.tab': 'Switched info-panel tab (listvy)',
    'column.toggle': 'Hid / showed the map column (listvy)',
    'help.toggle': 'Opened / closed the legend',
    'onboarding.done': 'Finished the intro tips',
    'onboarding.skip': 'Skipped the intro tips',
    'theme.toggle': 'Switched light / dark',
    'language.toggle': 'Switched language',
    'view.switch': 'Switched kartvy / listvy',
    'view.time': 'Time spent per view',
    'perf.listings': 'Listings loaded (performance)',
    'error.client': 'Hit a client error',
};
const CATEGORY_LABELS = {
    search: 'Search', map: 'Map', filter: 'Filters', listing: 'Listings', feed: 'Listing feed', calc: 'Tools',
    info: 'Tools', column: 'Tools', help: 'Tools', onboarding: 'Onboarding', theme: 'Settings', language: 'Settings',
    view: 'Views', perf: 'Health', error: 'Health',
};

const change = (current, previous) => {
    if (!previous) return current ? { text: 'new', tone: 'up' } : null;
    const pctChange = Math.round(((current - previous) / previous) * 100);
    if (pctChange === 0) return { text: '±0%', tone: 'flat' };
    return { text: `${pctChange > 0 ? '+' : ''}${pctChange}%`, tone: pctChange > 0 ? 'up' : 'down' };
};

const Kpi = ({ label, value, sub, current, previous, format = fmt }) => {
    const delta = change(current, previous);
    return (
        <div className="ax-tile">
            <div className="ax-tile-label">{label}</div>
            <div className="ax-tile-value">{format(current)}</div>
            <div className="ax-tile-sub">
                {delta && <span className={`ax-delta ax-delta--${delta.tone}`}>{delta.text}</span>}
                {delta && ' vs previous · '}
                {sub ?? (value !== undefined ? value : `was ${format(previous)}`)}
            </div>
        </div>
    );
};

const Breakdown = ({ data, title, hint, field = 'count', format = fmt, empty = 'No data in this period.' }) => {
    const rows = data || [];
    return (
        <section className="ax-card">
            <h3>{title}{hint && <span className="ax-hint"> {hint}</span>}</h3>
            <BarChart
                horizontal
                labels={rows.map((row) => String(row.value))}
                values={rows.map((row) => row[field])}
                format={format}
                emptyHint={empty}
            />
        </section>
    );
};

const Funnel = ({ title, steps }) => {
    const top = steps?.[0]?.sessions || 0;
    const labels = {
        sessions: 'Sessions', explored: 'Searched / filtered / picked an area', listing_open: 'Opened a listing',
        realtor_click: 'Clicked through to a realtor',
    };
    return (
        <section className="ax-card">
            <h3>{title}</h3>
            <div className="ax-funnel">
                {(steps || []).map((step) => {
                    const share = top ? Math.round((100 * step.sessions) / top) : 0;
                    return (
                        <div key={step.step} className="ax-funnel-row">
                            <span className="ax-funnel-label">{labels[step.step] || step.step}</span>
                            <span className="ax-hbar-track"><span className="ax-hbar-fill" style={{ width: `${share}%` }} /></span>
                            <span className="ax-funnel-value">{fmt(step.sessions)} <small className="ax-muted">({share}%)</small></span>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

// A listing's shareable link on the live site, from its key ("bostad/123" / "annons/123").
const listingKeyLink = (key) => {
    const match = String(key || '').match(/^(bostad|annons)\/(\d+)$/i);
    return match ? `https://xn--omrdeskollen-vcb.nu/?${match[1].toLowerCase()}=${match[2]}` : null;
};

// Top listings by one metric (the per-listing engagement rollup, /admin/analytics/listings).
const TopListings = ({ title, hint, rows, metric, visitorKey }) => (
    <section className="ax-card">
        <h3>{title}{hint && <span className="ax-hint"> {hint}</span>}</h3>
        <div className="ax-table-scroll">
            <table className="ax-table">
                <thead>
                    <tr><th>Listing</th><th className="num">{metric === 'opens' ? 'Opens' : metric === 'favorites' ? 'Favourites' : 'Realtor clicks'}</th><th className="num">Unique</th></tr>
                </thead>
                <tbody>
                    {rows.length === 0 && <tr><td colSpan={3}>No data in this period.</td></tr>}
                    {rows.map((row) => {
                        const link = listingKeyLink(row.key);
                        return (
                            <tr key={row.key}>
                                <td>{link ? <a href={link} target="_blank" rel="noreferrer">{row.key}</a> : row.key}</td>
                                <td className="num">{fmt(row[metric])}</td>
                                <td className="num">{visitorKey ? fmt(row[visitorKey]) : '—'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    </section>
);

const AnalyticsTab = () => {
    const [days, setDays] = useState(30);
    const [view, setView] = useState('overview');
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [listingStats, setListingStats] = useState(null);
    const [listingStatsError, setListingStatsError] = useState('');

    useEffect(() => {
        let active = true;
        setError('');
        fetchJson(`/admin/analytics?days=${days}`)
            .then((payload) => active && setData(payload))
            .catch((err) => active && setError(err.message));
        return () => {
            active = false;
        };
    }, [days]);

    // Per-listing engagement is a separate (and heavier) call: load it only when the Listings view is open.
    useEffect(() => {
        if (view !== 'listings') return undefined;
        let active = true;
        setListingStatsError('');
        setListingStats(null);
        fetchJson(`/admin/analytics/listings?days=${days}`)
            .then((payload) => active && setListingStats(payload || {}))
            .catch((err) => active && setListingStatsError(err.message));
        return () => {
            active = false;
        };
    }, [view, days]);

    const b = data?.breakdowns || {};
    const k = data?.kpis || {};
    const p = data?.previous || {};
    // map.area_popup counts opens and closes; map.area_compare (one per stretch of pinned cards) stands for the feature.
    const features = (data?.features || []).filter((row) => !['view.time', 'perf.listings', 'listing.close', 'map.area_popup'].includes(row.event));

    const listingRows = listingStats
        ? Object.entries(listingStats).map(([key, stats]) => ({ key, ...stats }))
        : [];
    const topBy = (metric) => [...listingRows].filter((row) => row[metric] > 0)
        .sort((a, bRow) => bRow[metric] - a[metric]).slice(0, 25);

    const overview = () => (
        <>
            {!k.sessions && (
                <div className="ax-empty">
                    <h2>No visits recorded yet</h2>
                    <p>Events appear here as soon as people use the site (tracking started with this release).</p>
                </div>
            )}
            <div className="ax-tiles">
                <Kpi label="Visitors" current={k.visitors} previous={p.visitors}
                    sub={`${fmt(k.new_visitors)} new · ${fmt(k.returning_visitors)} returning`} />
                <Kpi label="Sessions" current={k.sessions} previous={p.sessions}
                    sub={`${k.sessions_per_visitor || 0} per visitor`} />
                <Kpi label="Median engaged time" current={k.median_engaged_s} previous={p.median_engaged_s}
                    format={(v) => duration(v)} sub="active time per session" />
                <Kpi label="Searches" current={k.searches} previous={p.searches} sub="areas picked from search" />
                <Kpi label="Listings opened" current={k.listing_opens} previous={p.listing_opens}
                    sub={k.sessions ? `${(k.listing_opens / k.sessions).toFixed(1)} per session` : '—'} />
                <Kpi label="Realtor click-throughs" current={k.realtor_clicks} previous={p.realtor_clicks}
                    sub="outbound to the realtor's page" />
                <Kpi label="Conversion" current={k.conversion_rate} previous={p.conversion_rate}
                    format={(v) => `${v ?? 0}%`} sub={`${fmt(k.converted_sessions)} sessions clicked through`} />
                <Kpi label="Events" current={k.events} previous={p.events} sub="all tracked actions" />
            </div>

            <div className="ax-grid ax-grid--two">
                <section className="ax-card">
                    <h3>Sessions per day</h3>
                    <BarChart labels={data.daily.map((d) => d.day)} values={data.daily.map((d) => d.sessions)} />
                </section>
                <section className="ax-card">
                    <h3>Realtor click-throughs per day</h3>
                    <BarChart labels={data.daily.map((d) => d.day)} values={data.daily.map((d) => d.realtor_clicks)}
                        emptyHint="No click-throughs in this period." />
                </section>
            </div>

            <div className="ax-grid ax-grid--three">
                <Funnel title="Funnel: all sessions" steps={data.funnel.all} />
                <Funnel title="Funnel: kartvy" steps={data.funnel.kartvy} />
                <Funnel title="Funnel: listvy" steps={data.funnel.listvy} />
            </div>

            <section className="ax-card">
                <h3>Views <span className="ax-hint">kartvy vs listvy</span></h3>
                <div className="ax-table-scroll">
                    <table className="ax-table">
                        <thead>
                            <tr><th>View</th><th className="num">Sessions</th><th className="num">Active time</th>
                                <th className="num">Listings opened</th><th className="num">Realtor clicks</th>
                                <th className="num">Clicks per 100 opens</th></tr>
                        </thead>
                        <tbody>
                            {data.views.length === 0 && <tr><td colSpan={6}>No data in this period.</td></tr>}
                            {data.views.map((v) => (
                                <tr key={v.view}>
                                    <td>{v.view}</td>
                                    <td className="num">{fmt(v.sessions)}</td>
                                    <td className="num">{fmt(v.active_minutes)} min</td>
                                    <td className="num">{fmt(v.listing_opens)}</td>
                                    <td className="num">{fmt(v.realtor_clicks)}</td>
                                    <td className="num">{v.listing_opens ? Math.round((100 * v.realtor_clicks) / v.listing_opens) : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="ax-card">
                <h3>Feature adoption <span className="ax-hint">share of sessions that used each feature — what to invest in, what to drop</span></h3>
                <div className="ax-table-scroll">
                    <table className="ax-table">
                        <thead>
                            <tr><th>Area</th><th>Feature</th><th>Share of sessions</th><th className="num">Sessions</th>
                                <th className="num">Visitors</th><th className="num">Times used</th></tr>
                        </thead>
                        <tbody>
                            {features.length === 0 && <tr><td colSpan={6}>No data in this period.</td></tr>}
                            {features.map((f) => (
                                <tr key={f.event}>
                                    <td>{CATEGORY_LABELS[f.category] || f.category}</td>
                                    <td title={f.event}>{EVENT_LABELS[f.event] || f.event}</td>
                                    <td className="ax-share-cell">
                                        <span className="ax-hbar-track"><span className="ax-hbar-fill" style={{ width: `${Math.min(100, f.session_share)}%` }} /></span>
                                        <span>{f.session_share}%</span>
                                    </td>
                                    <td className="num">{fmt(f.sessions)}</td>
                                    <td className="num">{fmt(f.visitors)}</td>
                                    <td className="num">{fmt(f.count)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </>
    );

    const search = () => (
        <div className="ax-grid ax-grid--three">
            <Breakdown data={b['search.select:label']} title="Most searched areas" />
            <Breakdown data={b['search.select:type']} title="Search type" hint="kommun, ort, län, area…" />
            <Breakdown data={b['search.no_match:q']} title="Searches with no match" hint="content gaps" />
            <Breakdown data={b['search.select:kommun']} title="Searched kommuner" />
            <Breakdown data={b['search.select:position']} title="Picked suggestion position" hint="is the top row the one chosen?" />
            <Breakdown data={b['session.start:landing_view']} title="Landing view" />
        </div>
    );

    const filters = () => (
        <div className="ax-grid ax-grid--three">
            <Breakdown data={b['filter.apply:fields']} title="Filters used" hint="which fields people set" />
            <Breakdown data={b['map.metric:metric']} title="Map metric chosen" />
            <Breakdown data={b['filter.apply:score_metrics']} title="Area-score filters" />
            <Breakdown data={b['filter.apply:price_max']} title="Max price set" />
            <Breakdown data={b['filter.apply:rooms_min']} title="Min rooms set" />
            <Breakdown data={b['filter.apply:type']} title="Property type filter" />
            <Breakdown data={b['filter.apply:area_max']} title="Max area set" />
            <Breakdown data={b['filter.apply:fee_max']} title="Max fee set" />
            <Breakdown data={b['filter.detailed:on']} title="Detailed area scores" hint="the Detaljerat switch" />
        </div>
    );

    const areacards = () => (
        <div className="ax-grid ax-grid--three">
            <Breakdown data={b['map.area_compare:peak']} title="Most cards open at once" hint="per stretch: how many areas people compare side by side" />
            <Breakdown data={b['map.area_compare:opened']} title="Cards opened per stretch" />
            <Breakdown data={b['map.area_compare:seconds']} title="How long a stretch lasts" />
            <Breakdown data={b['map.area_popup:how']} title="How cards are closed" hint="double click, the card's ✕, or Stäng popups" />
            <Breakdown data={b['map.area_popup:open_count']} title="Cards open after each open or close" />
            <Breakdown data={b['map.area_compare:view']} title="Stretches by view" />
        </div>
    );

    const listings = () => (
        <>
            <div className="ax-grid ax-grid--three">
                <Breakdown data={b['listing.open:kommun']} title="Listings opened by kommun" />
                <Breakdown data={b['listing.open:price']} title="Listings opened by price" />
                <Breakdown data={b['listing.open:source']} title="Where listings are opened from" />
                <Breakdown data={b['listing.realtor_click:kommun']} title="Realtor clicks by kommun" />
                <Breakdown data={b['listing.realtor_click:price']} title="Realtor clicks by price" />
                <Breakdown data={b['listing.open:type']} title="Listings opened by type" />
            </div>
            <h2 className="ax-section-title">Per listing <span className="ax-hint">opens, favourites and realtor click-throughs for individual listings (addresses are in the Listings tab)</span></h2>
            {listingStatsError && <p className="ax-error">{listingStatsError}</p>}
            {!listingStats && !listingStatsError && <p className="ax-muted">Loading per-listing stats…</p>}
            {listingStats && (
                <div className="ax-grid ax-grid--three">
                    <TopListings title="Most opened listings" rows={topBy('opens')} metric="opens" visitorKey="opens_visitors" />
                    <TopListings title="Most clicked through" hint="to the realtor" rows={topBy('realtor')} metric="realtor" visitorKey="realtor_visitors" />
                    <TopListings title="Most favourited" rows={topBy('favorites')} metric="favorites" />
                </div>
            )}
        </>
    );

    const audience = () => (
        <div className="ax-grid ax-grid--three">
            <Breakdown data={b['session.start:referrer']} title="Where visitors come from" />
            <Breakdown data={b['session.start:utm_source']} title="Campaigns (utm_source)" />
            <Breakdown data={b['session.start:landing_view']} title="Landing view" />
            <Breakdown data={data.audience.device} title="Device" field="sessions" />
            <Breakdown data={data.audience.lang} title="Language" field="sessions" />
            <Breakdown data={data.audience.theme} title="Theme" field="sessions" />
        </div>
    );

    const health = () => (
        <div className="ax-grid ax-grid--two">
            <section className="ax-card">
                <h3>Listing load time <span className="ax-hint">kartvy, real visitors</span></h3>
                <dl className="ax-kv">
                    <dt>Median</dt><dd>{data.health.listing_load_ms_p50 != null ? `${fmt(data.health.listing_load_ms_p50)} ms` : '—'}</dd>
                    <dt>90th percentile</dt><dd>{data.health.listing_load_ms_p90 != null ? `${fmt(data.health.listing_load_ms_p90)} ms` : '—'}</dd>
                    <dt>Loads measured</dt><dd>{fmt(data.health.listing_loads)}</dd>
                    <dt>Client errors</dt><dd>{fmt(data.health.errors)} in {fmt(data.health.error_sessions)} sessions</dd>
                </dl>
            </section>
            <Breakdown data={b['error.client:message']} title="Most common client errors" empty="No errors in this period." />
        </div>
    );

    const VIEW_CONTENT = { overview, search, filters, areacards, listings, audience, health };

    return (
        <>
            <div className="ax-analytics-head">
                <div className="ax-segmented-buttons" role="group" aria-label="Period">
                    {PERIODS.map((period) => (
                        <button
                            key={period}
                            type="button"
                            className={period === days ? 'is-active' : ''}
                            onClick={() => setDays(period)}
                        >
                            Last {period} days
                        </button>
                    ))}
                </div>
                {data && (
                    <span className="ax-hint ax-hint--tight">
                        {data.start} – {data.end}, compared with the {days} days before.
                        {' '}Left out: {fmt(data.excluded?.bot_events)} bot and {fmt(data.excluded?.dev_events)} dev events.
                    </span>
                )}
            </div>
            <div className="ax-segmented-buttons ax-subnav" role="group" aria-label="Analytics view">
                {VIEWS.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        className={item.id === view ? 'is-active' : ''}
                        onClick={() => setView(item.id)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>
            {error && <p className="ax-error">{error}</p>}
            {!data && !error && <p className="ax-muted">Loading…</p>}
            {data && VIEW_CONTENT[view] && VIEW_CONTENT[view]()}
        </>
    );
};

export default AnalyticsTab;
